import DashboardType from '@/entities/DashboardType.js';
import { mapActions, mapState } from 'pinia';
import { useAccountStore } from '@/store/modules/account/store.js';
import { useEventBus } from '@/hooks/useEventBus.ts';
import { useNiotextStore } from '@/store/modules/niotext/store.ts';
import { ConversationKind } from '@/api/conversations/types/index.ts';
import { invalidateCustomerProfileLocally } from '@/components/customerProfile/invalidation/customerProfileInvalidation.ts';
import { showConnectionTimeout } from '../../utils/toastr.js';
import { useDashboardStore } from '../../store/modules/dashboard/store.js';
import { useGlobalStore } from '../../store/store.ts';
import { useContextStore } from '../../store/modules/context/store.ts';
import { useUserStore } from '../../store/modules/user/store.js';

export default {
    compatConfig: {
        MODE: 3,
        RENDER_FUNCTION: false,
    },

    render: () => null,

    setup() {
        const { emit } = useEventBus();

        return {
            eventBusEmit: emit,
        };
    },

    computed: {
        ...mapState(useDashboardStore, {
            dashboardType: 'dashboardType',
        }),
        ...mapState(useContextStore, {
            contextLead: 'lead',
        }),

        account() {
            return useAccountStore().contextAccount;
        },

        authUser() {
            return useUserStore().authUser;
        },
    },

    methods: {
        ...mapActions(useGlobalStore, [
            'commentCreated',
            'commentDeleted',
            'commentUpdated',
            'communicationCreated',
            'communicationDeleted',
            'communicationUpdated',
            'leadEmailCreated',
            'leadEmailDeleted',
            'leadEmailUpdated',
            'leadEventCreated',
            'leadPhoneCreated',
            'leadPhoneDeleted',
            'leadPhoneUpdated',
            'leadUpdatedAction',
            'leadVehicleCreated',
            'leadVehicleDeleted',
            'leadVehicleUpdated',
            'reloadLead',
            'taskEventCreated',
            'taskEventDeleted',
            'taskEventUpdated',
            'updateWebOrderBadgeCount',
            'deleteLead',
            'leadIsInDashboard',
        ]),
        ...mapActions(useContextStore, [
            'bulkLeadCommunicatingStarted',
            'bulkLeadCommunicatingEnded',
            'refreshSmsBadgeCountAction',
        ]),

        showEmailError(leadId) {
            let message = this.$t('toastr.errorMessages.sendingEmail');

            if (leadId) {
                const route = this.$router.resolve({
                    name: 'leads.update',
                    params: { id: leadId },
                });

                message += `
                    <a class="btn btn-warning" href="${route.href}" target="_blank">
                        ${this.$t('toastr.goToLead')}
                    </a>
                `;
            }

            this.$notify.warning({
                duration: -1,
                title: this.$t('toastr.error'),
                text: message,
            });
        },

        showAttachmentSizeTooBigError(maximumSize) {
            const message = this.$t('toastr.errorMessages.filesTooBig', {
                maximumSize,
            });

            this.$notify.warning({
                duration: -1,
                title: this.$t('toastr.error'),
                text: message,
            });
        },

        showInvalidImageError() {
            const message = this.$t('toastr.errorMessages.invalidImage');

            this.$notify.warning({
                duration: -1,
                title: this.$t('toastr.error'),
                text: message,
            });
        },

        showInvalidDataError() {
            const message = this.$t('toastr.errorMessages.invalidData');

            this.$notify.warning({
                duration: -1,
                title: this.$t('toastr.error'),
                text: message,
            });
        },

        listenToGlobal() {
            if (!this.$broadcasting.channels.global) {
                return;
            }

            this.$broadcasting.channels.global
                .listen('.reload', showConnectionTimeout)
                .listen('NetlifyDeployed', this._onNetlifyDeployed);
        },

        removeListenersFromGlobal() {
            if (!this.$broadcasting.channels.global) {
                return;
            }

            this.$broadcasting.channels.global
                .stopListening('.reload', showConnectionTimeout)
                .stopListening('NetlifyDeployed', this._onNetlifyDeployed);
        },

        _onNetlifyDeployed({ buildId }) {
            if (buildId != process.env.VUE_APP_BUILD_ID) {
                window.reloadNextPage = true;
            }
        },

        listenToAccount() {
            if (!this.$broadcasting.channels.account) {
                return;
            }

            // Lead
            this.$broadcasting.channels.account
                .listen('Lead.LeadUpdatedBroadcast', this._onLeadUpdatedBroadcast)
                .listen('Lead.LeadDeletedBroadcast', this._onLeadDeletedBroadcast);

            // Lead Event
            this.$broadcasting.channels.account.listen(
                'LeadEvent.LeadEventAddedToSalesJourneyBroadcast',
                this._onLeadEventAddedToSalesJourneyBroadcast
            );

            // Communication
            this.$broadcasting.channels.account
                .listen(
                    'Communication.CommunicationCreatedBroadcast',
                    this._onCommunicationCreatedBroadcast
                )
                .listen(
                    'Communication.CommunicationUpdatedBroadcast',
                    this._onCommunicationUpdatedBroadcast
                )
                .listen(
                    'Communication.CommunicationDeletedBroadcast',
                    this._onCommunicationDeletedBroadcast
                );

            // Communicating
            this.$broadcasting.channels.account
                .listen('LeadCommunicatingStarted', this._onLeadCommunicatingStarted)
                .listen('LeadCommunicatingEnded', this._onLeadCommunicatingEnded);

            // Lead Phone
            this.$broadcasting.channels.account
                .listen('LeadPhone.LeadPhoneCreatedBroadcast', this._onLeadPhoneCreatedBroadcast)
                .listen('LeadPhone.LeadPhoneUpdatedBroadcast', this._onLeadPhoneUpdatedBroadcast)
                .listen('LeadPhone.LeadPhoneDeletedBroadcast', this._onLeadPhoneDeletedBroadcast);

            // Lead Email
            this.$broadcasting.channels.account
                .listen('LeadEmail.LeadEmailCreatedBroadcast', this._onLeadEmailCreatedBroadcast)
                .listen('LeadEmail.LeadEmailUpdatedBroadcast', this._onLeadEmailUpdatedBroadcast)
                .listen('LeadEmail.LeadEmailDeletedBroadcast', this._onLeadEmailDeletedBroadcast);

            // Lead Vehicle
            this.$broadcasting.channels.account
                .listen(
                    'LeadVehicle.LeadVehicleCreatedBroadcast',
                    this._onLeadVehicleCreatedBroadcast
                )
                .listen(
                    'LeadVehicle.LeadVehicleUpdatedBroadcast',
                    this._onLeadVehicleUpdatedBroadcast
                )
                .listen(
                    'LeadVehicle.LeadVehicleDeletedBroadcast',
                    this._onLeadVehicleDeletedBroadcast
                );

            // Comment
            this.$broadcasting.channels.account
                .listen('Comment.CommentCreated', this._onCommentCreated)
                .listen('Comment.CommentUpdated', this._onCommentUpdated)
                .listen('Comment.CommentDeleted', this._onCommentDeleted);

            // TaskEvents
            this.$broadcasting.channels.account
                .listen('TaskEvent.TaskEventCreatedBroadcast', this._onTaskEventCreatedBroadcast)
                .listen('TaskEvent.TaskEventUpdatedBroadcast', this._onTaskEventUpdatedBroadcast)
                .listen('TaskEvent.TaskEventDeletedBroadcast', this._onTaskEventDeletedBroadcast);

            // In turn
            this.$broadcasting.channels.account.listen('UpdateInTurn', this._onUpdateInTurn);

            // Web Order
            this.$broadcasting.channels.account.listen(
                'WebOrderBadgeCountUpdated',
                this._onWebOrderBadgeCountUpdated
            );

            // Visit
            this.$broadcasting.channels.account
                .listen('Visit.VisitStarted', this._onVisitAltered)
                .listen('Visit.VisitEnded', this._onVisitAltered)
                .listen('Visit.VisitDiscarded', this._onVisitAltered);

            // SMS
            this.$broadcasting.channels.account.listen(
                'CommunicationMessage.NewSmsMessage',
                this._onNewSmsMessage
            );

            // SMS - Appointment Status
            this.$broadcasting.channels.account.listen(
                'CommunicationMessage.AppointmentStatusChanged',
                this._onAppointmentStatusChanged
            );

            // SMS - Archive
            this.$broadcasting.channels.account.listen(
                'Conversation.ConversationArchiveStateChanged',
                this._onConversationArchiveStateChanged
            );

            this.$broadcasting.channels.account.listen(
                'Customer.CustomerProfileProjectionCompleted',
                this._onCustomerProfileProjectionCompleted
            );
            this.$broadcasting.channels.account.listen(
                'Customer.CustomerContactUpdatedBroadcast',
                this._onCustomerContactUpdatedBroadcast
            );
            this.$broadcasting.channels.account.listen(
                'Customer.CustomerTaskEventChangedBroadcast',
                this._onCustomerTaskEventChangedBroadcast
            );
        },

        _onCustomerProfileProjectionCompleted({ customerId }) {
            invalidateCustomerProfileLocally({
                customerId,
                payloads: ['customer'],
            });
        },

        _onCustomerContactUpdatedBroadcast({ customerId }) {
            invalidateCustomerProfileLocally({
                customerId,
                payloads: ['customer'],
            });
        },

        _onCustomerTaskEventChangedBroadcast({ customerId }) {
            invalidateCustomerProfileLocally({
                customerId,
                payloads: ['leads', 'activity'],
            });
        },

        async _onLeadUpdatedBroadcast({ leadId, customerId, customerIds }) {
            this.reloadLead(leadId);
            this.updateDashboardLead(leadId);

            const affectedCustomerIds = [
                ...(Array.isArray(customerIds) ? customerIds : []),
                customerId,
            ].filter(
                (id, index, ids) => Number.isInteger(id) && id > 0 && ids.indexOf(id) === index
            );

            affectedCustomerIds.forEach((affectedCustomerId) => {
                invalidateCustomerProfileLocally({
                    customerId: affectedCustomerId,
                    payloads: ['leads', 'activity'],
                });
            });
        },

        _onLeadDeletedBroadcast(event) {
            const lead = JSON.parse(event.lead);
            this.updateDashboardLead(lead.id, true);
            this.deleteLead(lead.id, lead.customer_id);
        },

        _onLeadEventAddedToSalesJourneyBroadcast({ leadEvent }) {
            this.updateDashboardLead(leadEvent.lead_id);
            this.leadEventCreated(leadEvent);
        },

        _onCommunicationCreatedBroadcast(event) {
            const communication = JSON.parse(event.communication);

            this.updateDashboardLead(communication.lead_id);
            this.communicationCreated(communication);

            if (Number.isInteger(event.customerId) && event.customerId > 0) {
                invalidateCustomerProfileLocally({
                    customerId: event.customerId,
                    payloads: ['activity'],
                });
            }
        },
        _onCommunicationUpdatedBroadcast(event) {
            const communication = JSON.parse(event.communication);

            this.updateDashboardLead(communication.lead_id);
            this.communicationUpdated(communication);

            if (Number.isInteger(event.customerId) && event.customerId > 0) {
                invalidateCustomerProfileLocally({
                    customerId: event.customerId,
                    payloads: ['activity'],
                });
            }
        },
        _onCommunicationDeletedBroadcast(event) {
            const communication = JSON.parse(event.communication);

            this.updateDashboardLead(communication.lead_id);
            this.communicationDeleted(communication);

            if (Number.isInteger(event.customerId) && event.customerId > 0) {
                invalidateCustomerProfileLocally({
                    customerId: event.customerId,
                    payloads: ['activity'],
                });
            }
        },
        _onLeadCommunicatingStarted(event) {
            this.bulkLeadCommunicatingStarted(event.leads);
        },
        _onLeadCommunicatingEnded(event) {
            this.bulkLeadCommunicatingEnded(event.leads);
        },
        _onLeadPhoneCreatedBroadcast(event) {
            const leadPhone = JSON.parse(event.leadPhone);

            this.updateDashboardLead(leadPhone.lead_id);
            this.leadPhoneCreated(leadPhone);
        },
        _onLeadPhoneUpdatedBroadcast(event) {
            const leadPhone = JSON.parse(event.leadPhone);

            this.updateDashboardLead(leadPhone.lead_id);
            this.leadPhoneUpdated(leadPhone);
        },
        _onLeadPhoneDeletedBroadcast(event) {
            const leadPhone = JSON.parse(event.leadPhone);

            this.updateDashboardLead(leadPhone.lead_id);
            this.leadPhoneDeleted(leadPhone);
        },
        _onLeadEmailCreatedBroadcast(event) {
            const leadEmail = JSON.parse(event.leadEmail);

            this.updateDashboardLead(leadEmail.lead_id);
            this.leadEmailCreated(leadEmail);
        },
        _onLeadEmailUpdatedBroadcast(event) {
            const leadEmail = JSON.parse(event.leadEmail);

            this.updateDashboardLead(leadEmail.lead_id);
            this.leadEmailUpdated(leadEmail);
        },
        _onLeadEmailDeletedBroadcast(event) {
            const leadEmail = JSON.parse(event.leadEmail);

            this.updateDashboardLead(leadEmail.lead_id);
            this.leadEmailDeleted(leadEmail);
        },
        _onLeadVehicleCreatedBroadcast(event) {
            const leadVehicle = JSON.parse(event.leadVehicle);

            this.updateDashboardLead(leadVehicle.lead_id);
            this.leadVehicleCreated(leadVehicle);

            if (Number.isInteger(event.customerId) && event.customerId > 0) {
                invalidateCustomerProfileLocally({
                    customerId: event.customerId,
                    payloads: ['leads'],
                });
            }
        },
        async _onLeadVehicleUpdatedBroadcast({ leadId, leadVehicleId, customerId }) {
            this.updateDashboardLead(leadId);

            if (Number.isInteger(customerId) && customerId > 0) {
                invalidateCustomerProfileLocally({
                    customerId,
                    payloads: ['leads'],
                });
            }

            if (useContextStore().leadId != leadId && !this.leadIsInDashboard(leadId)) {
                return;
            }

            const leadVehicle = await this.$api.leadVehicles.show(leadVehicleId, {
                with: {
                    suppliers: '*',
                    customFields: ['custom_fields.id', 'custom_fields.fixed_field'],
                    appraisals: ['*'],
                    'appraisals.suppliers': ['supplier.id'],
                },
            });

            this.leadVehicleUpdated(leadVehicle);
        },
        _onLeadVehicleDeletedBroadcast(leadVehicle) {
            this.updateDashboardLead(leadVehicle.lead_id);
            this.leadVehicleDeleted(leadVehicle);

            if (Number.isInteger(leadVehicle.customerId) && leadVehicle.customerId > 0) {
                invalidateCustomerProfileLocally({
                    customerId: leadVehicle.customerId,
                    payloads: ['leads'],
                });
            }
        },
        _onCommentCreated({ comment, customerId }) {
            this.updateDashboardLead(comment.lead_id);
            this.commentCreated(comment);

            if (Number.isInteger(customerId) && customerId > 0) {
                invalidateCustomerProfileLocally({
                    customerId,
                    payloads: ['activity'],
                });
            }
        },
        _onCommentUpdated({ comment, customerId }) {
            this.updateDashboardLead(comment.lead_id);
            this.commentUpdated(comment);

            if (Number.isInteger(customerId) && customerId > 0) {
                invalidateCustomerProfileLocally({
                    customerId,
                    payloads: ['activity'],
                });
            }
        },
        _onCommentDeleted({ comment, customerId }) {
            this.updateDashboardLead(comment.lead_id);
            this.commentDeleted(comment);

            if (Number.isInteger(customerId) && customerId > 0) {
                invalidateCustomerProfileLocally({
                    customerId,
                    payloads: ['activity'],
                });
            }
        },
        _onTaskEventCreatedBroadcast(taskEvent) {
            this.updateDashboardLead(taskEvent.lead_id);
            this.taskEventCreated(taskEvent);
        },
        _onTaskEventUpdatedBroadcast(taskEvent) {
            this.updateDashboardLead(taskEvent.lead_id);
            this.taskEventUpdated(taskEvent);
        },
        _onTaskEventDeletedBroadcast({ taskEventId, leadId }) {
            this.updateDashboardLead(leadId);
            this.taskEventDeleted({ id: taskEventId, lead_id: leadId });
        },
        _onUpdateInTurn() {
            this.eventBusEmit('update-in-turn');
        },
        _onWebOrderBadgeCountUpdated({ badgeCount }) {
            this.updateWebOrderBadgeCount(badgeCount);
        },
        _onVisitAltered({ visit }) {
            this.eventBusEmit('visit-altered', visit);
        },
        _onCommunicationMessageRead({ conversationId, conversationUsers, customerId }) {
            const accountId = this.account?.id;

            if (accountId) {
                this.refreshSmsBadgeCountAction(accountId);
            }

            if (Number.isInteger(customerId) && customerId > 0) {
                invalidateCustomerProfileLocally({
                    customerId,
                    payloads: ['activity'],
                });
            }

            if (!conversationUsers) {
                return;
            }

            const niotextStore = useNiotextStore();
            niotextStore.updateConversationUsers(conversationId, conversationUsers);
        },
        _onSmsDeliveryReceipt(payload) {
            useNiotextStore().updateMessageStatus(
                payload.messageId,
                payload.status,
                payload.errorCode
            );
        },
        _onNewSmsMessage(payload) {
            const niotextStore = useNiotextStore();

            niotextStore.handleNewSmsMessageBroadcast(payload).finally(() => {
                if (
                    niotextStore.selectedConversationKind === ConversationKind.NATIVE &&
                    niotextStore.selectedConversationId === payload.conversationId
                ) {
                    return;
                }

                const accountId = this.account?.id;
                if (accountId) {
                    this.refreshSmsBadgeCountAction(accountId);
                }
            });
        },
        _onAppointmentStatusChanged(payload) {
            useNiotextStore().handleAppointmentStatusChanged(payload);
        },
        _onConversationArchiveStateChanged({ conversationId, archived }) {
            const niotextStore = useNiotextStore();
            niotextStore.updateConversationArchived(conversationId, archived);
        },
        removeListenersFromAccount() {
            if (!this.$broadcasting.channels.account) {
                return;
            }

            // Lead
            this.$broadcasting.channels.account
                .stopListening('Lead.LeadUpdatedBroadcast', this._onLeadUpdatedBroadcast)
                .stopListening('Lead.LeadDeletedBroadcast', this._onLeadDeletedBroadcast);

            this.$broadcasting.channels.account.stopListening(
                'Customer.CustomerProfileProjectionCompleted',
                this._onCustomerProfileProjectionCompleted
            );
            this.$broadcasting.channels.account.stopListening(
                'Customer.CustomerContactUpdatedBroadcast',
                this._onCustomerContactUpdatedBroadcast
            );
            this.$broadcasting.channels.account.stopListening(
                'Customer.CustomerTaskEventChangedBroadcast',
                this._onCustomerTaskEventChangedBroadcast
            );

            // Communication
            this.$broadcasting.channels.account
                .stopListening(
                    'Communication.CommunicationCreatedBroadcast',
                    this._onCommunicationCreatedBroadcast
                )
                .stopListening(
                    'Communication.CommunicationUpdatedBroadcast',
                    this._onCommunicationUpdatedBroadcast
                )
                .stopListening(
                    'Communication.CommunicationDeletedBroadcast',
                    this._onCommunicationDeletedBroadcast
                );

            // Communicating
            this.$broadcasting.channels.account
                .stopListening('LeadCommunicatingStarted', this._onLeadCommunicatingStarted)
                .stopListening('LeadCommunicatingEnded', this._onLeadCommunicatingEnded);

            // Lead Phone
            this.$broadcasting.channels.account
                .stopListening(
                    'LeadPhone.LeadPhoneCreatedBroadcast',
                    this._onLeadPhoneCreatedBroadcast
                )
                .stopListening(
                    'LeadPhone.LeadPhoneUpdatedBroadcast',
                    this._onLeadPhoneUpdatedBroadcast
                )
                .stopListening(
                    'LeadPhone.LeadPhoneDeletedBroadcast',
                    this._onLeadPhoneDeletedBroadcast
                );

            // Lead Email
            this.$broadcasting.channels.account
                .stopListening(
                    'LeadEmail.LeadEmailCreatedBroadcast',
                    this._onLeadEmailCreatedBroadcast
                )
                .stopListening(
                    'LeadEmail.LeadEmailUpdatedBroadcast',
                    this._onLeadEmailUpdatedBroadcast
                )
                .stopListening(
                    'LeadEmail.LeadEmailDeletedBroadcast',
                    this._onLeadEmailDeletedBroadcast
                );

            // Lead Vehicle
            this.$broadcasting.channels.account
                .stopListening(
                    'LeadVehicle.LeadVehicleCreatedBroadcast',
                    this._onLeadVehicleCreatedBroadcast
                )
                .stopListening(
                    'LeadVehicle.LeadVehicleUpdatedBroadcast',
                    this._onLeadVehicleUpdatedBroadcast
                )
                .stopListening(
                    'LeadVehicle.LeadVehicleDeletedBroadcast',
                    this._onLeadVehicleDeletedBroadcast
                );

            // Comment
            this.$broadcasting.channels.account
                .stopListening('Comment.CommentCreated', this._onCommentCreated)
                .stopListening('Comment.CommentUpdated', this._onCommentUpdated)
                .stopListening('Comment.CommentDeleted', this._onCommentDeleted);

            // TaskEvents
            this.$broadcasting.channels.account
                .stopListening(
                    'TaskEvent.TaskEventCreatedBroadcast',
                    this._onTaskEventCreatedBroadcast
                )
                .stopListening(
                    'TaskEvent.TaskEventUpdatedBroadcast',
                    this._onTaskEventUpdatedBroadcast
                )
                .stopListening(
                    'TaskEvent.TaskEventDeletedBroadcast',
                    this._onTaskEventDeletedBroadcast
                );

            // In turn
            this.$broadcasting.channels.account.stopListening('UpdateInTurn', this._onUpdateInTurn);

            // Web Order
            this.$broadcasting.channels.account.stopListening(
                'WebOrderBadgeCountUpdated',
                this._onWebOrderBadgeCountUpdated
            );

            // Visit
            this.$broadcasting.channels.account
                .stopListening('Visit.VisitStarted', this._onVisitAltered)
                .stopListening('Visit.VisitEnded', this._onVisitAltered)
                .stopListening('Visit.VisitDiscarded', this._onVisitAltered);

            // SMS
            this.$broadcasting.channels.account.stopListening(
                'CommunicationMessage.NewSmsMessage',
                this._onNewSmsMessage
            );

            // SMS - Appointment Status
            this.$broadcasting.channels.account.stopListening(
                'CommunicationMessage.AppointmentStatusChanged',
                this._onAppointmentStatusChanged
            );

            // SMS - Archive
            this.$broadcasting.channels.account.stopListening(
                'Conversation.ConversationArchiveStateChanged',
                this._onConversationArchiveStateChanged
            );
        },

        listenToUser() {
            if (!this.$broadcasting.channels.user) {
                return;
            }

            this.$broadcasting.channels.user
                .notification(this._onUserChannelNotification)
                .listen('LeadXpressEmailError', this._onLeadXpressEmailError)
                .listen(
                    'LeadXpressAttachmentSizeTooBigError',
                    this._onLeadXpressAttachmentSizeTooBigError
                )
                .listen('LeadXpressInvalidImage', this._onLeadXpressInvalidImage)
                .listen('LeadXpressInvalidData', this._onLeadXpressInvalidData)
                .listen('Notification.NewNotification', this._onNewNotification)
                .listen('Notification.NotificationSeen', this._onNotificationSeen)
                .listen('Notification.NotificationGroupSeen', this._onNotificationGroupSeen)
                .listen('ExportLeadError', this._onExportLeadError)
                .listen('CallStarted', this._onCallStarted)
                .listen('CallEnded', this._onCallEnded)
                .listen('ScreenPopOpen', this._onScreenPopOpen)
                .listen('ScreenPopClose', this._onScreenPopClose)
                .listen('ScreenPopVideoConferenceOpen', this._onScreenPopVideoConferenceOpen)
                .listen('ScreenPopVideoConferenceClose', this._onScreenPopVideoConferenceClose)
                .listen('UserSuspended', this._onUserSuspended)
                .listen('EmailConfig\\EmailConfigUpdated', this._onEmailConfigUpdated)
                .listen('BulkActionCompleted', this._onBulkActionCompleted)
                .listen(
                    'ExportLeadFromIntegrationsResult',
                    this._onExportLeadFromIntegrationsResult
                )
                .listen(
                    'Communication.CommunicationMessageReadBroadcast',
                    this._onCommunicationMessageRead
                )
                .listen('Sms.SmsCommunicationMessageDeliveryReceipt', this._onSmsDeliveryReceipt);

            this.$broadcasting.channels.user
                .listenForWhisper('closeAllTabs', this._onCloseAllTabs)
                .listenForWhisper('closeCallSummary', this._onCloseCallSummary);
        },

        removeListenersFromUser() {
            if (!this.$broadcasting.channels.user) {
                return;
            }

            this.$broadcasting.channels.user
                // notification method is an alias for listen('.Illuminate\Notifications\Events\BroadcastNotificationCreated')
                // but we don't have one alias to remove it.
                .stopListening(
                    '.Illuminate\\Notifications\\Events\\BroadcastNotificationCreated',
                    this._onUserChannelNotification
                )
                .stopListening('LeadXpressEmailError', this._onLeadXpressEmailError)
                .stopListening(
                    'LeadXpressAttachmentSizeTooBigError',
                    this._onLeadXpressAttachmentSizeTooBigError
                )
                .stopListening('LeadXpressInvalidImage', this._onLeadXpressInvalidImage)
                .stopListening('LeadXpressInvalidData', this._onLeadXpressInvalidData)
                .stopListening('Notification.NewNotification', this._onNewNotification)
                .stopListening('Notification.NotificationSeen', this._onNotificationSeen)
                .stopListening('Notification.NotificationGroupSeen', this._onNotificationGroupSeen)
                .stopListening('ExportLeadError', this._onExportLeadError)
                .stopListening('CallStarted', this._onCallStarted)
                .stopListening('CallEnded', this._onCallEnded)
                .stopListening('ScreenPopOpen', this._onScreenPopOpen)
                .stopListening('ScreenPopClose', this._onScreenPopClose)
                .stopListening('ScreenPopVideoConferenceOpen', this._onScreenPopVideoConferenceOpen)
                .stopListening(
                    'ScreenPopVideoConferenceClose',
                    this._onScreenPopVideoConferenceClose
                )
                .stopListening('UserSuspended', this._onUserSuspended)
                .stopListening('EmailConfig\\EmailConfigUpdated', this._onEmailConfigUpdated)
                .stopListening('BulkActionCompleted', this._onBulkActionCompleted)
                .stopListening(
                    'ExportLeadFromIntegrationsResult',
                    this._onExportLeadFromIntegrationsResult
                )
                .stopListening(
                    'Communication.CommunicationMessageReadBroadcast',
                    this._onCommunicationMessageRead
                )
                .stopListening(
                    'Sms.SmsCommunicationMessageDeliveryReceipt',
                    this._onSmsDeliveryReceipt
                );

            this.$broadcasting.channels.user
                .stopListeningForWhisper('closeAllTabs', this._onCloseAllTabs)
                .stopListeningForWhisper('closeCallSummary', this._onCloseCallSummary);
        },
        _onUserChannelNotification(notification) {
            switch (notification.type) {
                case 'App\\Notifications\\NextInTurn':
                case 'App\\Notifications\\NoUserInTurn':
                case 'App\\Notifications\\InTurnRemove':
                    this.eventBusEmit('notification-in-turn', notification);
                    break;
            }
        },
        _onLeadXpressEmailError(data) {
            const leadId = data.leadId != this.contextLead.id ? data.leadId : null;

            this.showEmailError(leadId);
        },
        _onLeadXpressAttachmentSizeTooBigError(data) {
            this.showAttachmentSizeTooBigError(data.maximumSize);
        },
        _onLeadXpressInvalidImage() {
            this.showInvalidImageError();
        },
        _onLeadXpressInvalidData() {
            this.showInvalidDataError();
        },
        _onNewNotification({ notification }) {
            this.eventBusEmit('new-notification', notification);
        },
        _onNotificationSeen({ notification }) {
            this.eventBusEmit('notification-seen', notification);
        },
        _onNotificationGroupSeen({ group }) {
            this.eventBusEmit('notification-group-seen', group);
        },
        _onExportLeadError(data) {
            this.eventBusEmit('export-lead-error', data);
        },

        _onCallStarted(event) {
            this.eventBusEmit('refresh-lead-call-sid', event);
        },

        async _onCallEnded(event) {
            this.eventBusEmit('close-call-interface');
            const lead = await this.$api.leads.show(event.leadId);

            if (this.account.call_summary && !(this.authUser?.skip_call_summary ?? false)) {
                const communication = await this.$api.communications.show(event.communicationId);
                this.eventBusEmit('show-call-summary', { lead, communication });
                // show-next-step will be emitted when call-summary modal is closed
            } else {
                this.eventBusEmit('show-next-step', {
                    lead,
                    afterCallNextStep: true,
                });
            }
        },
        _onScreenPopOpen(event) {
            this.eventBusEmit('screen-pop-open', { leadId: event.leadId, type: event.type });
        },
        _onScreenPopClose() {
            this.eventBusEmit('screen-pop-close');
        },
        _onScreenPopVideoConferenceOpen(event) {
            this.eventBusEmit('screen-pop-video-conference-open', event);
        },
        _onScreenPopVideoConferenceClose() {
            this.eventBusEmit('screen-pop-video-conference-close');
        },
        _onUserSuspended() {
            window.reloadNextPage = true;
        },
        _onEmailConfigUpdated(event) {
            this.eventBusEmit('email-config-updated', event);
        },
        _onBulkActionCompleted(event) {
            this.bulkActionCompleted(event);
        },
        _onExportLeadFromIntegrationsResult(event) {
            this.eventBusEmit('export-lead-from-integrations-result', event);
        },
        _onCloseAllTabs() {
            this.eventBusEmit('close-task-event');
        },
        _onCloseCallSummary() {
            this.eventBusEmit('close-call-summary');
        },

        updateDashboardLead(id, showIndicator = false) {
            if (this.dashboardType == DashboardType.ACTIVITY) {
                return;
            }

            if (showIndicator && this.leadIsInDashboard(id)) {
                this.$wait.start('indicator.leadTable');
            }

            this.eventBusEmit('fetch-stats', {
                passive: true,
                background: true,
            });
        },

        bulkActionCompleted(event) {
            let message = '';
            const route = this.$router.resolve({
                name: 'leads.update',
                params: { id: event.leadId },
            });

            switch (event.type) {
                case 'associated':
                case 'merge':
                    message += `
                        <button class="btn btn-warning" data-vue-router="${route.href}">
                            ${this.$t('toastr.goToLead')}
                        </button>
                    `;
                    this.reloadLead(event.leadId);

                    break;
                case 'failed':
                    this.$notify.error({
                        title: this.$t('toastr.error'),
                        text: event.message,
                    });

                    return;
            }

            message = event.message + message;

            this.$notify.success({
                title: this.$t('toastr.success'),
                text: message,
            });

            this.eventBusEmit('fetch-table-leads');
        },
    },

    created() {
        this.listenToGlobal();
        this.listenToAccount();
        this.listenToUser();

        this.$broadcasting.$on('global-channel-ready', this.listenToGlobal);
        this.$broadcasting.$on('account-channel-ready', this.listenToAccount);
        this.$broadcasting.$on('user-channel-ready', this.listenToUser);
    },

    beforeUnmount() {
        this.$broadcasting.$off('global-channel-ready', this.listenToGlobal);
        this.$broadcasting.$off('account-channel-ready', this.listenToAccount);
        this.$broadcasting.$off('user-channel-ready', this.listenToUser);

        this.removeListenersFromGlobal();
        this.removeListenersFromAccount();
        this.removeListenersFromUser();
    },
};
